import { useState, useEffect, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { Modal } from './Modal';
import { TagEditorModal } from './TagEditorModal';
import {
  FormInput,
  FormTextarea,
  FormSelect,
  FormRow,
  FormField,
  FormLabel,
  DayOfWeekCheckboxes,
  FormSection,
} from './FormFields';
import { PlacesSearchInput } from './PlacesSearchInput';
import { CollectionPickerModal } from './CollectionPickerModal';
import { CollectionEditorModal } from './CollectionEditorModal';
import { POIImportForm } from './POIImportForm';
import { TagIcon } from './TagIcon';
import { useUIStore } from '../store/uiStore';
import { useShallow } from 'zustand/react/shallow';
import { useLibraryStore } from '../store/libraryStore';
import { useCollectionsStore } from '../store/collectionsStore';
import type { IdealTimeOfDay, PlaceResult, Tag, PoiSource, CollectionVisibility } from '@poi-planner/shared';
import type { importItemToCreateData } from '../services/importService';
import { DEFAULT_PHOSPHOR_ICON, toPhosphorTagIcon } from '../utils/tagIcons';

type EditorMode = 'manual' | 'import';

// Type for tracking original form values (dirty state detection)
interface OriginalFormValues {
  name: string;
  lat: string;
  lng: string;
  timeBudget: string;
  tags: string[];
  primaryTagId: string | null;
  hasAvailability: boolean;
  availStart: string;
  availEnd: string;
  hasIdealTiming: boolean;
  idealDays: string[];
  idealTimeOfDay: string;
  hasWeather: boolean;
  weatherCondition: string;
  weatherSensitivity: string;
  notes: string;
  googleMapsUrl: string;
  sources: PoiSource[];
}

// Mini map pin preview matching MapView marker appearance
function MapPinPreview({ tag, size = 18 }: { tag: Tag; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: tag.color || '#111418',
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
      }}
    >
      <TagIcon icon={tag.icon} size={size * 0.55} whiteTint />
    </div>
  );
}

// Unified tag input with primary tag selection and autocomplete
function TagInputWithPrimary({
  tags,
  allTags,
  primaryTagId,
  onChange,
  onPrimaryChange,
  onEditTag,
  onCreateTag,
}: {
  tags: string[];
  allTags: Tag[];
  primaryTagId: string | null;
  onChange: (tags: string[]) => void;
  onPrimaryChange: (tagId: string | null) => void;
  onEditTag: (tag: Tag) => void;
  onCreateTag: (name: string) => void;
}) {
  const [input, setInput] = useState('');
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions based on input
  const suggestions = useMemo(() => {
    if (!input.trim()) return [];
    const inputLower = input.toLowerCase();
    return allTags.filter(
      (tag) =>
        tag.name.toLowerCase().includes(inputLower) &&
        !tags.some((t) => t.toLowerCase() === tag.name.toLowerCase())
    );
  }, [input, allTags, tags]);

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsAutocompleteOpen(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tagName?: string) => {
    const trimmed = (tagName || input).trim().toLowerCase();
    if (trimmed && !tags.some((t) => t.toLowerCase() === trimmed)) {
      // Auto-create tag if it doesn't exist in the library
      const existingTag = allTags.find((t) => t.name.toLowerCase() === trimmed);
      if (!existingTag) {
        onCreateTag(trimmed);
      }
      onChange([...tags, trimmed]);
    }
    setInput('');
    setIsAutocompleteOpen(false);
    setFocusedIndex(-1);
  };

  const selectSuggestion = (tag: Tag) => {
    addTag(tag.name);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
        selectSuggestion(suggestions[focusedIndex]);
      } else {
        addTag();
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setIsAutocompleteOpen(true);
        setFocusedIndex((prev) => (prev + 1) % suggestions.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setIsAutocompleteOpen(true);
        setFocusedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      }
    } else if (e.key === 'Escape') {
      setIsAutocompleteOpen(false);
      setFocusedIndex(-1);
    }
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const togglePrimary = (tagName: string) => {
    const tagEntity = allTags.find((t) => t.name.toLowerCase() === tagName.toLowerCase());
    if (!tagEntity) return;

    if (primaryTagId === tagEntity.id) {
      onPrimaryChange(null);
    } else {
      onPrimaryChange(tagEntity.id);
    }
  };

  // Filter out accommodation tag for display (handled separately)
  const displayTags = tags.filter((t) => t.toLowerCase() !== 'accommodation');

  return (
    <div ref={containerRef}>
      {/* Input row with autocomplete */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setIsAutocompleteOpen(true);
            setFocusedIndex(-1);
          }}
          onFocus={() => input.trim() && setIsAutocompleteOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Type and press Enter..."
          className="input-base w-full"
        />

        {/* Autocomplete dropdown */}
        {isAutocompleteOpen && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white rounded-lg border border-border shadow-lg z-50">
            {suggestions.map((tag, index) => (
              <div
                key={tag.id}
                onClick={() => selectSuggestion(tag)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={clsx(
                  'flex items-center gap-2 px-3 py-2 cursor-pointer',
                  focusedIndex === index ? 'bg-slate-50' : ''
                )}
              >
                <MapPinPreview tag={tag} size={18} />
                <span className="text-sm">{tag.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tags display */}
      {displayTags.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {displayTags.map((tagName) => {
            const tagEntity = allTags.find((t) => t.name.toLowerCase() === tagName.toLowerCase());
            const isPrimary = tagEntity && primaryTagId === tagEntity.id;
            const hasIcon = !!tagEntity;

            return (
              <span
                key={tagName}
                className={clsx(
                  'inline-flex items-center gap-1 pl-2 pr-1 py-1 text-xs font-semibold rounded-md transition-all',
                  isPrimary
                    ? 'bg-white border-2 border-text-primary text-text-primary ring-2 ring-text-primary/20 shadow-md'
                    : 'bg-input-bg text-text-secondary border-2 border-transparent',
                  hasIcon && !isPrimary && 'hover:scale-105 hover:shadow-sm'
                )}
              >
                {/* Icon + name - clickable to set primary */}
                <button
                  type="button"
                  onClick={() => hasIcon && togglePrimary(tagName)}
                  className={clsx(
                    'flex items-center gap-1.5 bg-transparent border-none p-0 font-semibold',
                    hasIcon ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
                    'text-text-secondary'
                  )}
                  title={hasIcon ? (isPrimary ? 'Click to remove map icon' : 'Click to use as map icon') : 'Configure in Tag Manager to use as icon'}
                >
                  {tagEntity && <MapPinPreview tag={tagEntity} size={20} />}
                  {tagName}
                  {isPrimary && (
                    <span className="material-symbols-outlined text-xs text-text-primary">location_on</span>
                  )}
                </button>

                {/* Edit/Remove button */}
                <button
                  type="button"
                  onClick={() => isPrimary && tagEntity ? onEditTag(tagEntity) : removeTag(tagName)}
                  title={isPrimary ? 'Edit tag' : 'Remove tag'}
                  className={clsx(
                    'flex items-center bg-transparent border-none cursor-pointer p-0.5 rounded transition-colors',
                    isPrimary
                      ? 'text-text-primary/70 hover:text-text-primary hover:bg-text-primary/10'
                      : 'text-text-muted hover:text-text-primary hover:bg-border'
                  )}
                >
                  <span className="material-symbols-outlined text-sm">
                    {isPrimary ? 'edit' : 'close'}
                  </span>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Hint text when no primary selected but tags with icons exist */}
      {!primaryTagId && displayTags.some((t) => allTags.some((at) => at.name.toLowerCase() === t.toLowerCase())) && (
        <p className="text-xs text-text-muted mt-2">
          Click a tag to use its icon on the map
        </p>
      )}
    </div>
  );
}

const TIME_OF_DAY_OPTIONS = [
  { value: '', label: '-- None --' },
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
];

// Sources editor component for tracking where POI recommendations came from
function SourcesEditor({
  sources,
  allSources,
  onChange,
}: {
  sources: PoiSource[];
  allSources: string[];
  onChange: (sources: PoiSource[]) => void;
}) {
  const [showAutocomplete, setShowAutocomplete] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowAutocomplete(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateSource = (index: number, field: 'text' | 'url', value: string) => {
    const newSources = [...sources];
    newSources[index] = { ...newSources[index], [field]: value };
    onChange(newSources);
  };

  const removeSource = (index: number) => {
    onChange(sources.filter((_, i) => i !== index));
  };

  const addSource = () => {
    onChange([...sources, { text: '', url: '' }]);
  };

  const selectAutocomplete = (index: number, text: string) => {
    updateSource(index, 'text', text);
    setShowAutocomplete(null);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {sources.map((source, index) => {
        const inputValue = source.text.toLowerCase();
        const suggestions = inputValue
          ? allSources.filter(
              (s) => s.toLowerCase().includes(inputValue) && s.toLowerCase() !== inputValue
            )
          : [];

        return (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1 flex flex-col gap-1.5">
              {/* Source name with autocomplete */}
              <div className="relative">
                <input
                  type="text"
                  value={source.text}
                  onChange={(e) => {
                    updateSource(index, 'text', e.target.value);
                    setShowAutocomplete(index);
                  }}
                  onFocus={() => source.text && setShowAutocomplete(index)}
                  placeholder="Source name (e.g., Mark's Tips, Eater NY)"
                  className="input-base w-full"
                />
                {/* Autocomplete dropdown */}
                {showAutocomplete === index && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 max-h-32 overflow-y-auto bg-white rounded-lg border border-border shadow-lg z-50">
                    {suggestions.slice(0, 5).map((text) => (
                      <div
                        key={text}
                        onClick={() => selectAutocomplete(index, text)}
                        className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
                      >
                        {text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* URL input */}
              <input
                type="url"
                value={source.url || ''}
                onChange={(e) => updateSource(index, 'url', e.target.value)}
                placeholder="Link (optional)"
                className="input-base w-full text-sm"
              />
            </div>
            {/* Remove button */}
            <button
              type="button"
              onClick={() => removeSource(index)}
              className="mt-2 p-1 text-text-muted hover:text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Remove source"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
        );
      })}
      {/* Add source button */}
      <button
        type="button"
        onClick={addSource}
        className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <span className="material-symbols-outlined text-lg">add</span>
        Add source
      </button>
    </div>
  );
}

const SENSITIVITY_OPTIONS = [
  { value: 'desired', label: 'Desired' },
  { value: 'required', label: 'Required' },
];

// Collections section component for managing POI collection memberships
function CollectionsSection({
  poiId,
  getCollectionsForPoi,
  removePoisFromCollection,
  onAddToCollection,
}: {
  poiId: string;
  getCollectionsForPoi: (poiId: string) => { id: string; name: string; visibility: CollectionVisibility }[];
  removePoisFromCollection: (collectionId: string, poiIds: string[]) => Promise<void>;
  onAddToCollection: () => void;
}) {
  const collections = getCollectionsForPoi(poiId);

  const handleRemove = async (collectionId: string) => {
    await removePoisFromCollection(collectionId, [poiId]);
  };

  const getVisibilityIcon = (visibility: CollectionVisibility) => {
    switch (visibility) {
      case 'private':
        return 'lock';
      case 'shared':
        return 'group';
      case 'public':
        return 'public';
    }
  };

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-lg text-text-secondary">folder_special</span>
          <span className="text-sm font-medium text-text-primary">Collections</span>
        </div>
        <button
          type="button"
          onClick={onAddToCollection}
          className="flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
        >
          <span className="material-symbols-outlined text-sm">add</span>
          Add to Collection
        </button>
      </div>

      {collections.length === 0 ? (
        <p className="text-xs text-text-muted">
          Not in any collections yet
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {collections.map((collection) => (
            <span
              key={collection.id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-800"
            >
              <span className="material-symbols-outlined text-xs text-amber-500">
                {getVisibilityIcon(collection.visibility)}
              </span>
              {collection.name}
              <button
                type="button"
                onClick={() => handleRemove(collection.id)}
                className="p-0.5 text-amber-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Remove from collection"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function POIEditorModal() {
  const { activeEditor, closePOIEditor } = useUIStore(
    useShallow((s) => ({ activeEditor: s.activeEditor, closePOIEditor: s.closePOIEditor }))
  );
  const poiEditorOpen = activeEditor?.type === 'poi';
  const poiEditorMode = poiEditorOpen ? activeEditor.mode : 'create';
  const poiEditorId = poiEditorOpen ? activeEditor.entityId : null;
  const { getPOI, createPOI, updatePOI, deletePOI, createTag, updateTag, tags: allTags, pois: allPois } = useLibraryStore();
  const { getCollectionsForPoi, removePoisFromCollection, createCollection } = useCollectionsStore();

  // Editor mode: manual entry or JSON import
  const [editorMode, setEditorMode] = useState<EditorMode>('manual');

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Original values for dirty state tracking (edit mode only)
  const originalValuesRef = useRef<OriginalFormValues | null>(null);

  // Collections modal state
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showCreateCollection, setShowCreateCollection] = useState(false);

  // Get all unique source texts for autocomplete
  const allSourceTexts = useMemo(() => {
    const sourceSet = new Set<string>();
    for (const poi of allPois) {
      for (const source of poi.sources ?? []) {
        if (source.text.trim()) {
          sourceSet.add(source.text.trim());
        }
      }
    }
    return Array.from(sourceSet).sort();
  }, [allPois]);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);

  const [name, setName] = useState('');
  const [primaryTagId, setPrimaryTagId] = useState<string | null>(null);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [timeBudget, setTimeBudget] = useState('60');
  const [tags, setTags] = useState<string[]>([]);
  const [hasAvailability, setHasAvailability] = useState(false);
  const [availStart, setAvailStart] = useState('');
  const [availEnd, setAvailEnd] = useState('');
  const [idealDays, setIdealDays] = useState<string[]>([]);
  const [idealTimeOfDay, setIdealTimeOfDay] = useState('');
  const [hasIdealTiming, setHasIdealTiming] = useState(false);
  const [hasWeather, setHasWeather] = useState(false);
  const [weatherCondition, setWeatherCondition] = useState('');
  const [weatherSensitivity, setWeatherSensitivity] = useState('desired');
  const [notes, setNotes] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [previousName, setPreviousName] = useState<string | null>(null);
  const [sources, setSources] = useState<PoiSource[]>([]);

  // Reset form when modal opens/changes
  useEffect(() => {
    if (!poiEditorOpen) return;

    // Reset to manual mode on open
    setEditorMode('manual');
    setShowDeleteConfirm(false);

    if (poiEditorMode === 'edit' && poiEditorId) {
      const poi = getPOI(poiEditorId);
      if (poi) {
        const latStr = poi.lat != null ? String(poi.lat) : '';
        const lngStr = poi.lng != null ? String(poi.lng) : '';
        const tagsArr = [...(poi.tags ?? [])];
        const hasAvail = !!poi.availability;
        const availStartStr = poi.availability?.start ?? '';
        const availEndStr = poi.availability?.end ?? '';
        const idealDaysArr = poi.idealDays ?? [];
        const idealTimeStr = poi.idealTimeOfDay ?? '';
        const hasIdeal = (poi.idealDays && poi.idealDays.length > 0) || !!poi.idealTimeOfDay;
        const hasWeatherReq = !!poi.weatherRequirement;
        const weatherCond = poi.weatherRequirement?.condition ?? '';
        const weatherSens = poi.weatherRequirement?.sensitivity ?? 'desired';
        const notesStr = poi.notes ?? '';
        const mapsUrl = poi.googleMapsUrl ?? '';
        const sourcesArr = poi.sources ?? [];

        setName(poi.name);
        setLat(latStr);
        setLng(lngStr);
        setTimeBudget(String(poi.timeBudget));
        setTags(tagsArr);
        setHasAvailability(hasAvail);
        setAvailStart(availStartStr);
        setAvailEnd(availEndStr);
        setIdealDays(idealDaysArr);
        setIdealTimeOfDay(idealTimeStr);
        setHasIdealTiming(hasIdeal);
        setHasWeather(hasWeatherReq);
        setWeatherCondition(weatherCond);
        setWeatherSensitivity(weatherSens);
        setNotes(notesStr);
        setGoogleMapsUrl(mapsUrl);
        setPrimaryTagId(poi.primaryTagId ?? null);
        setPreviousName(null);
        setSources(sourcesArr);

        // Store original values for dirty state tracking
        originalValuesRef.current = {
          name: poi.name,
          lat: latStr,
          lng: lngStr,
          timeBudget: String(poi.timeBudget),
          tags: tagsArr,
          primaryTagId: poi.primaryTagId ?? null,
          hasAvailability: hasAvail,
          availStart: availStartStr,
          availEnd: availEndStr,
          hasIdealTiming: hasIdeal,
          idealDays: idealDaysArr,
          idealTimeOfDay: idealTimeStr,
          hasWeather: hasWeatherReq,
          weatherCondition: weatherCond,
          weatherSensitivity: weatherSens,
          notes: notesStr,
          googleMapsUrl: mapsUrl,
          sources: sourcesArr,
        };
        return;
      }
    }
    // Create mode defaults
    setName('');
    setLat('');
    setLng('');
    setTimeBudget('60');
    setTags([]);
    setPrimaryTagId(null);
    setHasAvailability(false);
    setAvailStart('');
    setAvailEnd('');
    setIdealDays([]);
    setIdealTimeOfDay('');
    setHasIdealTiming(false);
    setHasWeather(false);
    setWeatherCondition('');
    setWeatherSensitivity('desired');
    setNotes('');
    setGoogleMapsUrl('');
    setPreviousName(null);
    setSources([]);
    originalValuesRef.current = null; // No original values in create mode
  }, [poiEditorOpen, poiEditorMode, poiEditorId, getPOI]);

  // Compute whether form has been modified (dirty state)
  const isDirty = useMemo(() => {
    // In create mode, always allow saving (if name is valid)
    if (!originalValuesRef.current) return true;

    const orig = originalValuesRef.current;
    return (
      name !== orig.name ||
      lat !== orig.lat ||
      lng !== orig.lng ||
      timeBudget !== orig.timeBudget ||
      primaryTagId !== orig.primaryTagId ||
      hasAvailability !== orig.hasAvailability ||
      availStart !== orig.availStart ||
      availEnd !== orig.availEnd ||
      hasIdealTiming !== orig.hasIdealTiming ||
      idealTimeOfDay !== orig.idealTimeOfDay ||
      hasWeather !== orig.hasWeather ||
      weatherCondition !== orig.weatherCondition ||
      weatherSensitivity !== orig.weatherSensitivity ||
      notes !== orig.notes ||
      googleMapsUrl !== orig.googleMapsUrl ||
      JSON.stringify([...tags].sort()) !== JSON.stringify([...orig.tags].sort()) ||
      JSON.stringify([...idealDays].sort()) !== JSON.stringify([...orig.idealDays].sort()) ||
      JSON.stringify(sources) !== JSON.stringify(orig.sources)
    );
  }, [
    name, lat, lng, timeBudget, tags, primaryTagId,
    hasAvailability, availStart, availEnd,
    hasIdealTiming, idealDays, idealTimeOfDay,
    hasWeather, weatherCondition, weatherSensitivity,
    notes, googleMapsUrl, sources
  ]);

  const handlePlaceSelect = (place: PlaceResult) => {
    // Store current name for undo (only if user typed something)
    if (name.trim() && name !== place.name) {
      setPreviousName(name);
    }
    setName(place.name);
    setLat(String(place.lat));
    setLng(String(place.lng));
    setGoogleMapsUrl(place.googleMapsUrl);
  };

  const handleUndoName = () => {
    if (previousName !== null) {
      setName(previousName);
      setPreviousName(null);
    }
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);

    const data = {
      name: name.trim(),
      lat: !isNaN(parsedLat) ? parsedLat : null,
      lng: !isNaN(parsedLng) ? parsedLng : null,
      timeBudget: parseInt(timeBudget) || 60,
      tags,
      primaryTagId,
      availability: hasAvailability && availStart && availEnd
        ? { start: availStart, end: availEnd }
        : null,
      idealDays: hasIdealTiming && idealDays.length > 0 ? idealDays : null,
      idealTimeOfDay: hasIdealTiming && idealTimeOfDay ? (idealTimeOfDay as IdealTimeOfDay) : null,
      weatherRequirement: hasWeather && weatherCondition.trim()
        ? { condition: weatherCondition.trim(), sensitivity: weatherSensitivity as 'required' | 'desired' }
        : null,
      notes: notes.trim() || null,
      googleMapsUrl: googleMapsUrl || undefined,
      sources: sources.filter((s) => s.text.trim()), // Only save non-empty sources
    };

    if (poiEditorMode === 'edit' && poiEditorId) {
      updatePOI(poiEditorId, data);
    } else {
      createPOI(data);
    }
    closePOIEditor();
  };

  const handleDelete = () => {
    if (poiEditorId) {
      deletePOI(poiEditorId);
      closePOIEditor();
    }
  };

  // Handle import from JSON
  const handleImport = (pois: ReturnType<typeof importItemToCreateData>[]) => {
    for (const poi of pois) {
      createPOI(poi);
    }
    closePOIEditor();
  };

  const isEdit = poiEditorMode === 'edit';
  const showImportTab = !isEdit; // Only show import tab in create mode

  return (
    <Modal
      open={poiEditorOpen}
      onClose={closePOIEditor}
      title={isEdit ? 'Edit POI' : 'Create POI'}
      icon="location_on"
      width="520px"
      footer={
        editorMode === 'import' ? null : (
          <>
            {isEdit && !showDeleteConfirm && (
              <button onClick={() => setShowDeleteConfirm(true)} className="btn-danger mr-auto">
                Delete
              </button>
            )}
            {isEdit && showDeleteConfirm && (
              <div className="flex items-center gap-2 mr-auto">
                <span className="text-sm text-text-secondary">Delete this POI?</span>
                <button onClick={handleDelete} className="btn-danger">
                  Yes
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
                  No
                </button>
              </div>
            )}
            <button onClick={closePOIEditor} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!name.trim() || (isEdit && !isDirty)}
              className={clsx(
                'px-5 py-2 text-[13px] font-semibold font-sans rounded-lg border-none cursor-pointer transition-all',
                name.trim() && (!isEdit || isDirty)
                  ? 'bg-text-primary text-white'
                  : 'bg-border text-text-muted cursor-default'
              )}
            >
              {isEdit ? 'Save' : 'Create'}
            </button>
          </>
        )
      }
    >
      {/* Mode toggle (only in create mode) */}
      {showImportTab && (
        <div className="flex gap-1 p-1 bg-surface-secondary rounded-lg mb-4">
          <button
            onClick={() => setEditorMode('manual')}
            className={clsx(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all',
              editorMode === 'manual'
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            )}
          >
            <span className="material-symbols-outlined text-lg">edit</span>
            Manual
          </button>
          <button
            onClick={() => setEditorMode('import')}
            className={clsx(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all',
              editorMode === 'import'
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            )}
          >
            <span className="material-symbols-outlined text-lg">upload_file</span>
            Import JSON
          </button>
        </div>
      )}

      {/* Import mode */}
      {editorMode === 'import' && showImportTab && (
        <POIImportForm
          onImport={handleImport}
          onCancel={() => setEditorMode('manual')}
        />
      )}

      {/* Manual mode */}
      {editorMode === 'manual' && (
      <div className="flex flex-col gap-4">
        <FormField label="Name">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <FormInput value={name} onChange={(v) => { setName(v); setPreviousName(null); }} placeholder="e.g., Central Park" />
            </div>
            {previousName !== null && (
              <button
                type="button"
                onClick={handleUndoName}
                title={`Restore: ${previousName}`}
                className="p-2 bg-input-bg border border-border rounded-md cursor-pointer flex items-center justify-center transition-colors hover:bg-border"
              >
                <span className="material-icons text-base text-text-secondary">undo</span>
              </button>
            )}
          </div>
        </FormField>

        <FormField label="Search Location">
          <PlacesSearchInput
            onSelect={handlePlaceSelect}
            placeholder="Search Google Places..."
          />
        </FormField>

        <FormRow>
          <FormField label="Latitude" flex={1}>
            <FormInput value={lat} onChange={setLat} type="number" step="any" placeholder="40.7829" />
          </FormField>
          <FormField label="Longitude" flex={1}>
            <FormInput value={lng} onChange={setLng} type="number" step="any" placeholder="-73.9654" />
          </FormField>
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in Google Maps"
              className="group mt-[22px] px-3 py-2 flex items-center gap-2 no-underline rounded-lg cursor-pointer transition-all duration-200 ease-out
                bg-gradient-to-r from-emerald-500 to-teal-500
                hover:from-emerald-400 hover:to-teal-400
                hover:shadow-lg hover:shadow-emerald-500/25 hover:-translate-y-0.5
                active:translate-y-0 active:shadow-md"
            >
              <span className="material-symbols-outlined text-[15px] text-white/90 group-hover:text-white transition-colors">
                location_on
              </span>
              <span className="text-[13px] font-semibold text-white tracking-tight whitespace-nowrap">
                View in Maps
              </span>
              <span className="material-symbols-outlined text-[13px] text-white/70 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all">
                arrow_outward
              </span>
            </a>
          )}
        </FormRow>

        <FormField label="Time Budget (minutes)">
          <FormInput value={timeBudget} onChange={setTimeBudget} type="number" min="0" placeholder="60" />
        </FormField>

        {/* Accommodation Toggle */}
        <div className="flex items-center justify-between py-2 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-lg text-text-secondary">hotel</span>
            <span className="text-sm font-medium text-text-primary">Use as Accommodation</span>
          </div>
          <button
            type="button"
            onClick={() => {
              const hasAccomTag = tags.some((t) => t.toLowerCase() === 'accommodation');
              if (hasAccomTag) {
                setTags(tags.filter((t) => t.toLowerCase() !== 'accommodation'));
              } else {
                setTags([...tags, 'accommodation']);
              }
            }}
            className={clsx(
              'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
              tags.some((t) => t.toLowerCase() === 'accommodation')
                ? 'bg-text-primary'
                : 'bg-border'
            )}
          >
            <span
              className={clsx(
                'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                tags.some((t) => t.toLowerCase() === 'accommodation')
                  ? 'translate-x-6'
                  : 'translate-x-1'
              )}
            />
          </button>
        </div>
        {tags.some((t) => t.toLowerCase() === 'accommodation') && (
          <p className="text-xs text-text-muted -mt-2">
            This location can be assigned as a sleep destination. Configure color and check-in/out times per trip.
          </p>
        )}

        {/* Unified Tags + Map Icon selector */}
        <div>
          <FormLabel>Tags</FormLabel>

          {/* Tag input */}
          <TagInputWithPrimary
            tags={tags}
            allTags={allTags}
            primaryTagId={primaryTagId}
            onChange={(newTags) => {
              setTags(newTags);
              // Clear primary tag if it was removed
              if (primaryTagId) {
                const primaryTag = allTags.find((t) => t.id === primaryTagId);
                if (primaryTag && !newTags.some((t) => t.toLowerCase() === primaryTag.name.toLowerCase())) {
                  setPrimaryTagId(null);
                }
              }
            }}
            onPrimaryChange={setPrimaryTagId}
            onEditTag={setEditingTag}
            onCreateTag={(name) => createTag({ name, icon: toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON) })}
          />
        </div>

        <FormSection label="Availability Window" enabled={hasAvailability} onToggle={setHasAvailability}>
          <FormRow>
            <FormField label="Start" flex={1}>
              <FormInput value={availStart} onChange={setAvailStart} type="date" />
            </FormField>
            <FormField label="End" flex={1}>
              <FormInput value={availEnd} onChange={setAvailEnd} type="date" />
            </FormField>
          </FormRow>
        </FormSection>

        <FormSection label="Ideal Days & Time" enabled={hasIdealTiming} onToggle={setHasIdealTiming}>
          <div className="flex flex-col gap-3">
            <FormField label="Ideal Days">
              <DayOfWeekCheckboxes selected={idealDays} onChange={setIdealDays} />
            </FormField>
            <FormField label="Ideal Time of Day">
              <FormSelect value={idealTimeOfDay} onChange={setIdealTimeOfDay} options={TIME_OF_DAY_OPTIONS} />
            </FormField>
          </div>
        </FormSection>

        <FormSection label="Weather Requirement" enabled={hasWeather} onToggle={setHasWeather}>
          <div className="flex flex-col gap-2.5">
            <FormField label="Condition">
              <FormInput value={weatherCondition} onChange={setWeatherCondition} placeholder="e.g., clear, no rain" />
            </FormField>
            <FormField label="Sensitivity">
              <FormSelect value={weatherSensitivity} onChange={setWeatherSensitivity} options={SENSITIVITY_OPTIONS} />
            </FormField>
          </div>
        </FormSection>

        <FormField label="Notes">
          <FormTextarea value={notes} onChange={setNotes} placeholder="Any additional notes..." />
        </FormField>

        {/* Sources section */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="material-symbols-outlined text-lg text-text-secondary">bookmark</span>
            <span className="text-sm font-medium text-text-primary">Sources</span>
            <span className="text-xs text-text-muted">(where did you find this?)</span>
          </div>
          <SourcesEditor
            sources={sources}
            allSources={allSourceTexts}
            onChange={setSources}
          />
        </div>

        {/* Collections section (only for existing POIs) */}
        {isEdit && poiEditorId && (
          <CollectionsSection
            poiId={poiEditorId}
            getCollectionsForPoi={getCollectionsForPoi}
            removePoisFromCollection={removePoisFromCollection}
            onAddToCollection={() => setShowCollectionPicker(true)}
          />
        )}
      </div>
      )}

      {/* Tag Editor Modal */}
      <TagEditorModal
        open={!!editingTag}
        onClose={() => setEditingTag(null)}
        tag={editingTag ?? undefined}
        onSave={async (data) => {
          if (editingTag) {
            await updateTag(editingTag.id, data);
          }
        }}
      />

      {/* Collection Picker Modal */}
      {poiEditorId && (
        <CollectionPickerModal
          open={showCollectionPicker}
          onClose={() => setShowCollectionPicker(false)}
          poiIds={[poiEditorId]}
          onCreateNew={() => setShowCreateCollection(true)}
        />
      )}

      {/* Create Collection Modal */}
      <CollectionEditorModal
        open={showCreateCollection}
        onClose={() => setShowCreateCollection(false)}
        onSave={async (data) => {
          await createCollection(data.name, data.description, data.visibility);
        }}
      />
    </Modal>
  );
}
